import {
  Dialog,
  Stack,
  Typography,
  IconButton,
  Divider,
  Box,
  Button,
  lighten,
} from "@mui/material";
import { useCallback } from "react";
import { CrossIcon } from "@/components/common/icons";
import SlideBottomDialog from "@/components/common/modals/slideBottomDialog";
import { ReadOnlyQuill } from "@/components/common/quill/readOnlyQuill";
import {
  PRIMARY,
  TEXT_PRIMARY_DARK,
  TEXT_SECONDARY,
  VIOLET,
} from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { formattedPrice } from "@/utils";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";
import PackageCardHeader from "./packageCardHeader";
import PackageItemsList from "./packageItemsList";
import PackageStoreButton from "./packageStoreButton";

type PackageDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  packageItem: MedspaPackage;
  isFeaturedDeal?: boolean;
  packageQuantity: number;
  onAmountChange: (change: 1 | -1) => void;
  onInputChange: (value: string) => void;
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
  setIsCheckoutDrawerOpen?: (isOpen: boolean) => void;
  setIsOpen?: (isOpen: boolean) => void;
};

function Content({
  packageItem,
  isFeaturedDeal,
}: Omit<PackageDetailsModalProps, "isOpen" | "onClose">) {
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");
  const {
    packageName,
    packagePrice,
    packageItems,
    packageSavings,
    description,
    additionalInfo,
    endDate,
  } = packageItem;

  const thumbnailUrl = additionalInfo?.thumbnail ?? "/assets/images/gift.png";

  return (
    <Stack spacing={3}>
      <PackageCardHeader
        thumbnailUrl={thumbnailUrl}
        packageSavings={Number(packageSavings)}
        isFeaturedDeal={isFeaturedDeal}
        endDate={endDate}
        isMobile={isMobile}
      />
      <Stack spacing={2} px={isMobile ? 0 : 2} pb={2}>
        <Stack>
          <Typography variant="h3" color={TEXT_PRIMARY_DARK}>
            {packageName}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="labelDefault"
              color={TEXT_PRIMARY_DARK}
              fontWeight="600"
            >
              {formattedPrice(packagePrice)}
            </Typography>
            {Number(packageSavings) > 0 && (
              <Typography
                variant={isMobile ? "labelDefault" : "labelSmall"}
                sx={{
                  textDecoration: "line-through",
                  color: TEXT_SECONDARY,
                }}
              >
                {formattedPrice(Number(packagePrice) + Number(packageSavings))}
              </Typography>
            )}
          </Stack>
        </Stack>

        {description && (
          <ReadOnlyQuill
            value={description}
            sx={{ fontSize: "paragraph.fontSize" }}
          />
        )}

        <Divider />

        <PackageItemsList packageItems={packageItems} showAll variant="modal" />
      </Stack>
    </Stack>
  );
}

export default function PackageDetailsModal({
  isOpen,
  setIsOpen,
  onClose,
  packageItem,
  isFeaturedDeal,
  packageQuantity,
  onAmountChange,
  onInputChange,
  customizationEnabled,
  customizationPrimaryColor,
  setIsCheckoutDrawerOpen,
}: PackageDetailsModalProps) {
  const isMobile = useMoxieMediaQuery("ipad-hor", "down");
  const isPackageInCart = packageQuantity > 0;

  const handleGoToCart = useCallback(() => {
    setIsCheckoutDrawerOpen(true);
    setIsOpen(false);
  }, [setIsCheckoutDrawerOpen, setIsOpen]);

  if (isMobile) {
    return (
      <SlideBottomDialog
        title="Package Details"
        open={isOpen}
        submitButtonText="Close"
        onClose={onClose}
        onSubmit={onClose}
        withActions={false}
      >
        <Content
          packageItem={packageItem}
          isFeaturedDeal={isFeaturedDeal}
          packageQuantity={packageQuantity}
          onAmountChange={onAmountChange}
          onInputChange={onInputChange}
          customizationEnabled={customizationEnabled}
          customizationPrimaryColor={customizationPrimaryColor}
          setIsCheckoutDrawerOpen={setIsCheckoutDrawerOpen}
          setIsOpen={setIsOpen}
        />

        <PackageStoreButton
          fullWidth
          size="large"
          variant="modal"
          packageQuantity={packageQuantity}
          onAmountChange={onAmountChange}
          onInputChange={onInputChange}
          unitPrice={Number(packageItem?.packagePrice)}
          isMobile={isMobile}
          customizationEnabled={customizationEnabled}
          customizationPrimaryColor={customizationPrimaryColor}
        />

        {isPackageInCart && (
          <Stack sx={{ gap: 2, mt: 2, flexDirection: "row" }}>
            <Button variant="outlined" fullWidth onClick={onClose}>
              Continue shopping
            </Button>

            <Button variant="contained" fullWidth onClick={handleGoToCart}>
              Go to cart
            </Button>
          </Stack>
        )}
      </SlideBottomDialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      PaperProps={{
        sx: {
          width: 400,
          p: 1,
          gap: 3,
          borderRadius: 4,
          margin: 0,
          borderWidth: 0,
        },
      }}
      onClose={onClose}
    >
      <Stack sx={{ gap: 2 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            margin: 1.5,
            zIndex: 1,
            backgroundColor: "white",
            borderRadius: 1.5,
            "&:hover": {
              backgroundColor: customizationEnabled
                ? lighten(customizationPrimaryColor, 0.85)
                : VIOLET[20],
            },
          }}
        >
          <CrossIcon
            size={24}
            color={customizationEnabled ? customizationPrimaryColor : PRIMARY}
          />
        </IconButton>
        <Content
          packageItem={packageItem}
          isFeaturedDeal={isFeaturedDeal}
          packageQuantity={packageQuantity}
          onAmountChange={onAmountChange}
          onInputChange={onInputChange}
          customizationEnabled={customizationEnabled}
          customizationPrimaryColor={customizationPrimaryColor}
          setIsCheckoutDrawerOpen={setIsCheckoutDrawerOpen}
          setIsOpen={setIsOpen}
        />
        <Box px={2} pb={2}>
          <PackageStoreButton
            fullWidth
            packageQuantity={packageQuantity}
            onAmountChange={onAmountChange}
            onInputChange={onInputChange}
            unitPrice={Number(packageItem?.packagePrice)}
            isMobile={isMobile}
            customizationEnabled={customizationEnabled}
            customizationPrimaryColor={customizationPrimaryColor}
          />
        </Box>
      </Stack>
    </Dialog>
  );
}
